import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../services/api';

/**
 * Aplicar el arranque a los expedientes que YA están en la cartera.
 *
 * Encender un canal aquí **no arranca nada** en lo que ya se subió: el arranque
 * de cada expediente lo aplicó su tarea una sola vez y, si el canal estaba
 * apagado, lo saltó y se cerró. Como el correo se configura en otra pantalla y
 * casi siempre después de subir la cartera, le pasa a casi todos.
 *
 * ── Por qué es un botón y no un efecto del interruptor ─────────────────────
 * Porque pone a recibir a una parte de la cartera. Que eso lo dispare un toggle
 * sin decir a cuántos afecta es demasiado para un clic, y va contra el
 * fail-closed del resto del canal. Así que: el número delante, y confirmar.
 *
 * Solo entra el canal que **nunca** arrancó en ese expediente. Uno a mitad de
 * camino no se re-arranca: recibiría otra vez el primer aviso.
 */

const CANALES = [
  ['email', 'Correo'],
  ['whatsapp', 'WhatsApp'],
  ['llamada', 'Llamada'],
  ['sms', 'SMS'],
];

const ArranqueExistentes = ({ companyId, esAdmin, onAplicado }) => {
  const [datos, setDatos] = useState(null);
  const [elegidos, setElegidos] = useState([]);
  const [confirmando, setConfirmando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const cargar = useCallback(async () => {
    if (esAdmin && !companyId) return;
    try {
      const { data } = await api.get(
        '/ar-arranque/existing',
        esAdmin ? { params: { company_id: companyId } } : undefined
      );
      setDatos(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [esAdmin, companyId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const alternar = canal =>
    setElegidos(prev => (prev.includes(canal) ? prev.filter(c => c !== canal) : [...prev, canal]));

  // Lo que se va a tocar de verdad: un canal elegido sin pendientes no suma.
  const aTocar = elegidos.reduce((n, c) => n + (datos?.canales?.[c]?.pendientes || 0), 0);

  const aplicar = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post(
        '/ar-arranque/existing',
        { canales: elegidos },
        esAdmin ? { params: { company_id: companyId } } : undefined
      );
      setResultado(data.data.canales);
      setElegidos([]);
      setConfirmando(false);
      await cargar();
      if (onAplicado) onAplicado();
      toast.success('Arranque aplicado');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAplicando(false);
    }
  };

  // Sin datos, o con una respuesta que no es la esperada, este bloque
  // simplemente no sale. Es un añadido a la pantalla de Configuración: que un
  // endpoint secundario conteste raro no puede tumbar la pantalla entera, que es
  // donde se configuran los cuatro canales.
  if (!datos?.canales) return null;

  const nadaQueHacer = datos.total_ar === 0;

  return (
    <section className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
      <h2 className='font-semibold text-slate-800'>Los expedientes que ya están en la cartera</h2>
      <p className='text-sm text-slate-500 mt-1'>
        Lo de arriba se aplica a los expedientes <strong>nuevos</strong>. Los que ya estaban arrancaron una sola
        vez, cuando se importaron: si entonces un canal estaba apagado, se lo saltaron y no se vuelve a
        intentar. Desde aquí se les aplica ahora.
      </p>

      {nadaQueHacer ? (
        <p className='text-sm text-slate-400 mt-3'>Esta empresa no tiene expedientes de AR activos.</p>
      ) : (
        <>
          <ul className='mt-3 divide-y divide-slate-100'>
            {CANALES.map(([canal, nombre]) => {
              const c = datos.canales[canal] || { pendientes: 0, ya_arrancados: 0 };
              return (
                <li key={canal} className='py-2 flex items-center gap-3'>
                  <input
                    type='checkbox'
                    id={`existentes-${canal}`}
                    checked={elegidos.includes(canal)}
                    disabled={!datos.can_apply || c.pendientes === 0 || aplicando}
                    onChange={() => alternar(canal)}
                    className='rounded-lg border-slate-300 disabled:opacity-40'
                  />
                  <label htmlFor={`existentes-${canal}`} className='text-sm text-slate-700 w-24'>
                    {nombre}
                  </label>
                  <span className='text-sm text-slate-500'>
                    {c.pendientes === 0 ? (
                      'ninguno pendiente'
                    ) : (
                      <>
                        <strong className='text-slate-800'>{c.pendientes}</strong> sin arrancar
                      </>
                    )}
                    {c.ya_arrancados > 0 && ` · ${c.ya_arrancados} ya arrancados`}
                  </span>
                </li>
              );
            })}
          </ul>

          {!datos.can_apply && (
            <p className='text-xs text-slate-400 mt-2'>
              Solo la empresa puede aplicarlo: es su cartera la que empieza a recibir.
            </p>
          )}

          {datos.can_apply && !confirmando && (
            <button
              type='button'
              disabled={aTocar === 0 || aplicando}
              onClick={() => setConfirmando(true)}
              className='mt-3 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed'
            >
              {aTocar === 0 ? 'Elige un canal' : `Aplicar a ${aTocar} expediente${aTocar === 1 ? '' : 's'}`}
            </button>
          )}

          {/* El número delante, y una frase que dice qué va a pasar de verdad. */}
          {confirmando && (
            <div className='mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2'>
              <p className='text-sm text-amber-900 flex items-start gap-2'>
                <ExclamationTriangleIcon className='w-4 h-4 shrink-0 mt-0.5' />
                <span>
                  Vas a poner <strong>{aTocar}</strong> expediente{aTocar === 1 ? '' : 's'} a recibir por{' '}
                  {elegidos.map(c => CANALES.find(([k]) => k === c)?.[1]).join(', ')}. Los deudores empezarán a
                  recibir según lo que hayas configurado arriba.
                  {aTocar > datos.max && ` Se harán ${datos.max} ahora y el resto en otra pasada.`}
                </span>
              </p>
              <div className='flex items-center gap-2 mt-2'>
                <button
                  type='button'
                  onClick={aplicar}
                  disabled={aplicando}
                  className='px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:bg-amber-300'
                >
                  {aplicando ? 'Aplicando…' : 'Sí, aplicarlo'}
                </button>
                <button
                  type='button'
                  onClick={() => setConfirmando(false)}
                  disabled={aplicando}
                  className='text-xs text-slate-600 hover:text-slate-900'
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Qué pasó. Los motivos importan: «0 arrancados» sin el porqué no
              dice si el canal está apagado o si simplemente no había ninguno. */}
          {resultado && (
            <div className='mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2'>
              <p className='text-xs font-medium text-slate-500 mb-1 flex items-center gap-1'>
                <ArrowPathIcon className='w-3.5 h-3.5' />
                Resultado
              </p>
              {Object.entries(resultado).map(([canal, r]) => (
                <p key={canal} className='text-xs text-slate-600'>
                  <strong>{CANALES.find(([k]) => k === canal)?.[1] || canal}</strong>: {r.arrancados} arrancado
                  {r.arrancados === 1 ? '' : 's'}
                  {r.restantes > 0 && ` · ${r.restantes} para la siguiente pasada`}
                  {Object.keys(r.motivos || {}).length > 0 &&
                    ` · sin arrancar: ${Object.entries(r.motivos)
                      .map(([m, n]) => `${n} ${m}`)
                      .join(', ')}`}
                </p>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ArranqueExistentes;
